import React from "react";
import { Col, Row } from "antd";
import { getWeek } from "helpers/functions";
import { BookingSeason } from "containers/Company/EventBooking/interface";
import PriceTable from "containers/Company/EventBooking/PriceTable/PriceTable";
import { StyledCard, StyledTextDiv, StyledTextDivBold } from "./style";

const NUMBER_WORDS: Record<number, string> = { 2: "to", 3: "tre", 4: "fire", 5: "fem", 6: "seks" };

interface Props {
  season: BookingSeason | undefined;
}

const BookingInfoCard: React.FC<Props> = ({ season }) => {
  const semester = season ? `${season.is_fall ? "høsten" : "våren"} ${season.year}` : undefined;
  // The 4th year Indøk class trip is in the spring. The week and dates in the text must be updated by hand each year.
  const showClassTrip = season !== undefined && !season.is_fall;
  const tierCount = season?.price_tiers.length ?? 0;
  const tierCountText = NUMBER_WORDS[tierCount] ?? String(tierCount);

  return (
    <>
      <StyledCard>
        <Row gutter={25}>
          <Col xs={24} sm={24} md={12}>
            <StyledTextDivBold>Når kan vi arrangere bedriftspresentasjon?</StyledTextDivBold>
            <StyledTextDiv>
              {season ? (
                <>
                  Bedriftspresentasjonssesongen for {semester} varer fra uke {getWeek(new Date(season.semester_start))}{" "}
                  til og med uke {getWeek(new Date(season.semester_end))}, med unntak av utvalgte datoer. I
                  bookingkalenderen kan dere se hvilke datoer som er tilgjengelige.
                </>
              ) : (
                <>Informasjon om neste sesong kommer snart.</>
              )}
            </StyledTextDiv>
            {showClassTrip && (
              <StyledTextDiv>
                I uke 11 (15.03–19.03) er 4. klasse Indøk på klassetur, og vil dermed ikke kunne delta på
                bedriftspresentasjon. Merk at vi oppfordrer generelt til å rette mye av rekrutteringsinnsatsen mot
                tidligere kull, siden det er da studentene gjør seg opp meninger.
              </StyledTextDiv>
            )}

            <StyledTextDivBold>Hvilken ukedag bør vi velge?</StyledTextDivBold>
            <StyledTextDiv>
              Mandag til torsdag starter presentasjonen vanligvis 17.15, men det er også mulig å utforske muligheter på
              dagtid avhengig av studentenes fagplan - her bistår Bindeleddet gjerne.
            </StyledTextDiv>
            <StyledTextDiv>
              Torsdager er en populær dag for bedriftspresentasjoner der bedrifter gjerne kombinerer presentasjonen med
              en tur på byen i etterkant av opplegget.
            </StyledTextDiv>
            <StyledTextDiv>
              På fredager velger mange bedrifter å starte opplegg rundt lunsjtider, her er mulighetene mange for enten
              faglige eller sosiale opplegg med studentene.
            </StyledTextDiv>
            <StyledTextDiv>
              Bindeleddet har også god innsikt i andre arrangementer i Trondheim og muligheter for spennende opplegg. Vi
              ønsker å jobbe tett med dere for å lage noe som både står i stil med bedriftens kultur og engasjerer
              studentene. Spør oss gjerne om tips for hvilken dag som passer opplegget deres best.
            </StyledTextDiv>

            <StyledTextDivBold>Eksamenslunsj</StyledTextDivBold>
            <StyledTextDiv>
              Eksamenslunsj er et arrangement der studentene kan bli bedre kjent med representanter fra bedriften over
              en hyggelig lunsjbuffet i Kjelkantina. Ettersom det foregår i eksamensperioden, er det et ønske om at
              arrangementet er uten presentasjon.
            </StyledTextDiv>
            <StyledTextDiv>
              Ved spørsmål eller ønske om å booke eksamenslunsj, ta kontakt med{" "}
              <a href="mailto:bedrift@bindeleddet.no">bedrift@bindeleddet.no</a>.
            </StyledTextDiv>

            <StyledTextDivBold>Hva er inkludert når vi booker en bedriftspresentasjon?</StyledTextDivBold>
            <StyledTextDiv>
              <ul>
                <li>Bestilling av egnede lokaler til presentasjon og bespisning.</li>
                <li>Bistand i planlegging av presentasjon og sosialt opplegg.</li>
                <li>Markedsføring av presentasjonen gjennom plakater og nettsider.</li>
                <li>Kvalitetssikring av det tekniske opplegget.</li>
                <li>
                  Påmeldingssystem via app og nettside med mulighet for å sette av plasser for ønskede linjer og
                  klassetrinn.
                </li>
                <li>Statistikk over påmeldte studenter.</li>
                <li>Stand sentralt på campus.</li>
                <li>Fullstendig tilbakemeldingsrapport av bedriftspresentasjonen.</li>
              </ul>
            </StyledTextDiv>

            <StyledTextDivBold>Hvilket opplegg bør vi gjennomføre?</StyledTextDivBold>
            <StyledTextDiv>
              Ved booking av bedriftspresentasjon vil vi hjelpe til med å planlegge arrangementet i samarbeid med dere.
              Vi vil kontakte dere i god tid før arrangementet.
            </StyledTextDiv>
            <StyledTextDiv>
              Bespisning kan enten være i Kjelkantinen på campus, eller holdes på en restaurant i Trondheim. Det er også
              mulig å avholde selve presentasjonen eksternt på samme sted som bespisningen. På dagtid kan dere stå på
              stand eller gjennomføre arrangementer som mockintervjuer eller case. Dette tas godt imot av studentene, og
              anbefales!
            </StyledTextDiv>
            <StyledTextDiv>
              Bindeleddet har mye erfaring med hva som engasjerer og er populært blant både bedrifter og studenter, bruk
              oss for det vi er verdt.
            </StyledTextDiv>
          </Col>

          <Col xs={24} sm={24} md={12}>
            <StyledTextDivBold>Hvilke studenter når vi ut til?</StyledTextDivBold>
            <StyledTextDiv>
              Bindeleddet NTNU er bedriftskontakten for Indøk, men vi samarbeider tett med de andre linjeforeningene på
              Gløshaugen. Å åpne presentasjonen for andre linjer gjør vi gledelig, men vi har et krav om at minst
              halvparten av plassene skal være reservert for indøkstudenter. Markedsføring mot de andre linjene utover
              informasjon i arrangementet på våre egne nettsider kan gjøres mot et gebyr til den respektive
              linjeforening.
            </StyledTextDiv>

            <StyledTextDivBold>Hvor mye koster det å arrangere en bedriftspresentasjon?</StyledTextDivBold>
            <StyledTextDiv>
              {season && tierCount > 0 ? (
                <>
                  {tierCount === 1 ? "Det er én pris" : `Det finnes ${tierCountText} ulike pristrinn`} for
                  bedriftspresentasjon med Bindeleddet {semester}:
                  <PriceTable tiers={season.price_tiers} />
                </>
              ) : (
                <>Prisene for neste sesong kommer snart.</>
              )}
            </StyledTextDiv>
            <StyledTextDiv>
              Prisene er i NOK og ekskludert mva. Trykk på en dato i bookingkalenderen for å se hvilken pris som gjelder
              den valgte datoen.
            </StyledTextDiv>
            <StyledTextDiv>
              Differansen i pris mellom de ulike trinnene kommer hovedsakelig av at antall oppmøtte studenter kan synke
              noe mot slutten av sesongen. Torsdager har et ekstra tillegg ettersom dette er en svært populær dag for
              studenter å dra på bedriftspresentasjon. Ellers vil vi understreke at tilbudet fra Bindeleddet fremdeles
              er helt likt på de ulike trinnene.
            </StyledTextDiv>
            <StyledTextDiv>
              Bespisning og eventuelle aktiviteter vil komme utenom, og vil avhenge av antall studenter som deltar på
              presentasjonen og opplegget dere ønsker. Vi er behjelpelige med å finne et passende sted for deres
              bedrift.
            </StyledTextDiv>

            <StyledTextDivBold>Hvordan håndterer Bindeleddet NTNU personvern?</StyledTextDivBold>
            <StyledTextDiv>
              Bindeleddet NTNU tar personvern på alvor og følger GDPR-retningslinjene nøye. Dette innebærer at vi ikke
              deler personopplysninger om studentene som deltar på deres bedriftspresentasjoner med bedriftene.
            </StyledTextDiv>

            <StyledTextDivBold>Andre spørsmål?</StyledTextDivBold>
            <StyledTextDiv>
              Dersom dere har andre spørsmål kan dere sende oss en mail på{" "}
              <a href="mailto:bedrift@bindeleddet.no">bedrift@bindeleddet.no</a>!
            </StyledTextDiv>

            <StyledTextDivBold>
              NB! Ved åpning av bookingen opplever vi mye trafikk på nettsidene. Dersom du får problemer med å booke kan
              du ringe bedriftsleder i Bindeleddet, Olav Bjelland på 955 26 220.
            </StyledTextDivBold>
          </Col>
        </Row>
      </StyledCard>
    </>
  );
};

export default BookingInfoCard;
